<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'

import type { DnsConnectState, DnsConnectStatus } from '@/composables/domainConfig'

const props = defineProps<{
  emailDomainId: number
}>()

const emit = defineEmits<{
  (e: 'applied'): void
}>()

const { t } = useI18n()
const { getDnsConnect } = useDomainConfig()

const POLLING_INTERVAL = 3000
const MAX_POLLING_DURATION = 2 * 60 * 1000
const IN_PROGRESS: DnsConnectStatus[] = ['pending', 'consented', 'applying']

const state = ref<DnsConnectState | null>(null)
const hasTimedOut = ref(false)
let pollingTimer: ReturnType<typeof setTimeout> | null = null
let pollingStartTime = 0

const connection = computed(() => state.value?.connection ?? null)
const status = computed(() => connection.value?.status ?? null)
const isInProgress = computed(() => !!status.value && IN_PROGRESS.includes(status.value))
const showNextStep = computed(() => status.value === 'failed' || status.value === 'needs_review')

const severity = computed<'success' | 'warn' | 'error' | 'info' | 'secondary'>(() => {
  if (hasTimedOut.value) return 'warn'

  switch (status.value) {
    case 'applied':
      return 'success'
    case 'needs_review':
      return 'warn'
    case 'failed':
    case 'rollback_failed':
      return 'error'
    case 'refused':
      return 'info'
    default:
      return 'secondary'
  }
})

const title = computed(() =>
  hasTimedOut.value
    ? t('pages.domain.config.dnsConnect.timeout.title')
    : t(`pages.domain.config.dnsConnect.status.${status.value}.title`)
)

const description = computed(() => {
  if (hasTimedOut.value) return t('pages.domain.config.dnsConnect.timeout.description')

  const zone = connection.value?.zone
  if (status.value === 'failed') {
    return t(`pages.domain.config.dnsConnect.errors.${connection.value?.error ?? 'unknown'}`, { zone })
  }

  return t(`pages.domain.config.dnsConnect.status.${status.value}.description`, { zone })
})

const fetchState = async () => {
  const next = await getDnsConnect(props.emailDomainId)
  if (next) state.value = next

  if (next && !isInProgress.value) {
    if (status.value === 'applied' || status.value === 'needs_review') emit('applied')
    return
  }

  if (Date.now() - pollingStartTime < MAX_POLLING_DURATION) {
    pollingTimer = setTimeout(fetchState, POLLING_INTERVAL)
  } else {
    hasTimedOut.value = true
  }
}

onMounted(() => {
  pollingStartTime = Date.now()
  fetchState()
})

onUnmounted(() => {
  if (pollingTimer) clearTimeout(pollingTimer)
})
</script>

<template>
  <BaseTip v-if="status || hasTimedOut" :severity="severity" class="custom_message">
    <template v-if="isInProgress && !hasTimedOut" #icon>
      <Loader2 class="animate-spin" />
    </template>
    <template #text>
      <div class="flex flex-col gap-2">
        <span class="font-medium">{{ title }}</span>
        <span>{{ description }}</span>

        <ul v-if="status === 'needs_review' && connection?.review.length" class="mt-1 flex flex-col gap-2">
          <li v-for="item in connection.review" :key="`${item.key}-${item.hostname}`" class="flex flex-col gap-1">
            <span class="font-medium">
              {{ t(`pages.domain.config.dnsConnect.review.${item.reason}`) }}
              <span class="font-mono font-normal">{{ item.hostname }}</span>
            </span>
            <span v-if="item.current.length">
              {{ t('pages.domain.config.dnsConnect.review.current') }}
              <span v-for="value in item.current" :key="value" class="block font-mono break-all">{{ value }}</span>
            </span>
            <span>
              {{ t('pages.domain.config.dnsConnect.review.expected') }}
              <span class="block font-mono break-all">{{ item.expected }}</span>
            </span>
          </li>
        </ul>

        <span v-if="showNextStep && !hasTimedOut" class="text-sm">
          {{ t('pages.domain.config.dnsConnect.nextStep') }}
        </span>
      </div>
    </template>
  </BaseTip>
</template>
